import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Command,
  Users,
  Laptop,
  FileText,
  Megaphone,
  Phone,
  CalendarDays,
  ArrowRight,
  Sparkles,
  Smartphone,
  Tablet,
  Clock,
  ExternalLink,
  CornerDownLeft,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import AiLoadingOverlay from './AiLoadingOverlay';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import { isPrivilegedRole } from '../config/comingSoonRoutes';

/* ─── Iconos por grupo (búsqueda clásica) ───────────────────────────────── */
const GROUP_ICONS = {
  empleados: Users,
  equipos: Laptop,
  documentos: FileText,
  publicaciones: Megaphone,
  extensiones: Phone,
  salas: CalendarDays,
};

/* ─── Detección de modo IA ───────────────────────────────────────────────── */
const IA_TRIGGERS = /(\?|¿|cuánt|cuant|cuándo|cuando|quién|quien|dime|dame|lista|resumen|muestra|busca|qué|que\s+\w+\s+tiene|por\s+qué|porqué|explica|compara|analiza)/i;
const MIN_WORDS_IA = 4;

function isIAQuery(text) {
  const trimmed = text.trim();
  if (trimmed.startsWith('/ia ')) return true;
  if (IA_TRIGGERS.test(trimmed)) return true;
  if (trimmed.split(/\s+/).length >= MIN_WORDS_IA) return true;
  return false;
}

/* ─── Mapa de tipo → ruta de navegación ──────────────────────────────────── */
const TIPO_ROUTES = {
  empleado: '/empleados',
  asset: '/equipos',
  smartphone: '/smartphones',
  tablet: '/tablets',
};

const TIPO_ICONS = {
  empleado: Users,
  asset: Laptop,
  smartphone: Smartphone,
  tablet: Tablet,
};

/* ─── Componente de loading IA ────────────────────────────────────────────── */
function IALoading() {
  return (
    <div style={{ padding: '1rem 0' }}>
      <AiLoadingOverlay
        inline
        phases={['Consultando la base de datos...', 'Analizando resultados...', 'Generando respuesta...']}
        size="sm"
      />
    </div>
  );
}

/* ─── Componente de resultado IA ─────────────────────────────────────────── */
function IAResult({ respuesta, onNavigate, onFollowUp }) {
  const followUpRef = useRef(null);
  const [followUp, setFollowUp] = useState('');

  const handleFollowUpSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const q = followUp.trim();
    if (!q) return;
    setFollowUp('');
    onFollowUp(q);
  };

  const handleFollowUpKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      followUpRef.current?.blur();
    }
  };

  if (!respuesta) return null;

  return (
    <div className="p-5 space-y-4">
      {/* Texto de respuesta */}
      <div className="text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none">
        <ReactMarkdown>{respuesta.texto}</ReactMarkdown>
      </div>

      {/* Datos interactivos */}
      {respuesta.datos && respuesta.datos.length > 0 && (
        <div className="space-y-1.5">
          {respuesta.datos.map((item, idx) => {
            const Icon = TIPO_ICONS[item.tipo] || FileText;
            const route = TIPO_ROUTES[item.tipo];
            const isClickable = route && item.id;

            return (
              <button
                key={idx}
                type="button"
                disabled={!isClickable}
                onClick={() => isClickable && onNavigate(route)}
                className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-all ${
                  isClickable
                    ? 'border-slate-200 hover:border-violet-300 hover:bg-violet-50/50 cursor-pointer'
                    : 'border-slate-100 bg-slate-50/50 cursor-default'
                }`}
              >
                <div className={`flex-shrink-0 rounded-md p-1.5 ${
                  isClickable ? 'bg-violet-100 text-violet-600' : 'bg-slate-100 text-slate-400'
                }`}>
                  <Icon size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {item.titulo}
                  </p>
                  {item.subtitulo && (
                    <p className="text-xs text-slate-500 truncate">{item.subtitulo}</p>
                  )}
                </div>
                {isClickable && (
                  <ExternalLink size={12} className="flex-shrink-0 text-slate-400" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Metadata */}
      {respuesta.meta && (
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="flex items-center gap-1">
            <Clock size={10} />
            {respuesta.meta.tiempo_ms < 1000
              ? `${respuesta.meta.tiempo_ms}ms`
              : `${(respuesta.meta.tiempo_ms / 1000).toFixed(1)}s`
            }
          </span>
          {respuesta.meta.filas > 0 && (
            <span>{respuesta.meta.filas} registro{respuesta.meta.filas !== 1 ? 's' : ''}</span>
          )}
        </div>
      )}

      {/* Input de seguimiento */}
      <form onSubmit={handleFollowUpSubmit} className="flex items-center gap-2">
        <input
          ref={followUpRef}
          value={followUp}
          onChange={(e) => setFollowUp(e.target.value)}
          onKeyDown={handleFollowUpKeyDown}
          placeholder="Pregunta de seguimiento..."
          className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none placeholder:text-slate-400 focus:border-violet-300 focus:bg-white transition-colors"
        />
        <button
          type="submit"
          disabled={!followUp.trim()}
          className="flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <CornerDownLeft size={12} />
        </button>
      </form>
    </div>
  );
}

/* ─── Componente principal ───────────────────────────────────────────────── */
function UniversalSearchModal({ enabled = true }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Estado modo IA
  const [iaMode, setIaMode] = useState(false);
  const [iaLoading, setIaLoading] = useState(false);
  const [iaResponse, setIaResponse] = useState(null);
  const [iaHistory, setIaHistory] = useState([]);
  const [iaError, setIaError] = useState(null);

  // La búsqueda con IA consulta la base de datos directamente: solo admin y ti.
  // Para el resto, el modal queda en búsqueda clásica aunque escriban una pregunta.
  const { user } = useAuth();
  const puedeUsarIA = isPrivilegedRole(user?.role);

  const detectedIA = useMemo(() => puedeUsarIA && isIAQuery(query), [puedeUsarIA, query]);

  const results = useMemo(() => {
    return groups.flatMap((group) =>
      group.items.map((item) => ({ ...item, groupKey: group.key, groupLabel: group.label }))
    );
  }, [groups]);

  // ─── Abrir/cerrar modal ─────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event) => {
      const isShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (isShortcut) {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  // ─── Búsqueda clásica ───────────────────────────────────────────────────
  useEffect(() => {
    if (!open || iaMode || detectedIA) return;
    const q = query.trim();
    const timer = setTimeout(async () => {
      if (q.length < 2) {
        setGroups([]);
        setLoading(false);
        setSelectedIndex(0);
        return;
      }
      try {
        setLoading(true);
        const { data } = await axiosInstance.get('/api/search/global', {
          params: { q, limit: 6 },
        });
        setGroups(data.groups || []);
        setSelectedIndex(0);
      } catch {
        setGroups([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(timer);
  }, [open, query, iaMode, detectedIA]);

  // ─── Consulta IA ────────────────────────────────────────────────────────
  const executeIAQuery = useCallback(async (pregunta, history = []) => {
    setIaLoading(true);
    setIaError(null);
    setIaResponse(null);
    try {
      const { data } = await axiosInstance.post('/api/ia/consulta', {
        pregunta,
        historial: history,
      });

      if (data.error) {
        setIaError(data.error);
        setIaResponse(null);
      } else {
        setIaResponse(data);
        setIaError(null);
      }

      // Actualizar historial
      const newHistory = [
        ...history,
        { role: 'user', content: pregunta },
        { role: 'assistant', content: data.texto || data.error || '' },
      ];
      setIaHistory(newHistory);
    } catch (err) {
      const msg = err.response?.data?.error || 'No se pudo conectar con el servicio de IA.';
      setIaError(msg);
      setIaResponse(null);
    } finally {
      setIaLoading(false);
    }
  }, []);

  // ─── Keyboard navigation ───────────────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }

      // Solo navegar en modo clásico
      if (iaMode || iaResponse || iaLoading) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (results.length > 0) setSelectedIndex((prev) => (prev + 1) % results.length);
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (results.length > 0) setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
      }
      if (event.key === 'Enter' && !iaMode && !detectedIA) {
        const selected = results[selectedIndex];
        if (!selected) return;
        event.preventDefault();
        navigate(selected.path);
        setOpen(false);
      }
      if (event.key === 'Enter' && (iaMode || detectedIA)) {
        const q = query.trim();
        if (q.length < 3) return;
        event.preventDefault();
        setIaMode(true);
        executeIAQuery(q, iaHistory);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, navigate, results, selectedIndex, iaMode, detectedIA, query, iaHistory, iaResponse, iaLoading, executeIAQuery]);

  // ─── Helpers ────────────────────────────────────────────────────────────
  const close = () => {
    setOpen(false);
    setQuery('');
    setGroups([]);
    setSelectedIndex(0);
    setIaMode(false);
    setIaLoading(false);
    setIaResponse(null);
    setIaHistory([]);
    setIaError(null);
  };

  const openResult = (item) => {
    navigate(item.path);
    close();
  };

  const handleIANavigate = (path) => {
    navigate(path);
    close();
  };

  const handleFollowUp = (pregunta) => {
    setQuery(pregunta);
    executeIAQuery(pregunta, iaHistory);
  };

  const toggleIAMode = () => {
    setIaMode((prev) => !prev);
    setIaResponse(null);
    setIaError(null);
    setIaHistory([]);
    setGroups([]);
  };

  if (!enabled || !open) return null;

  const showIA = iaMode || detectedIA;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-start justify-center bg-slate-900/55 px-4 pt-[10vh] backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        {/* ─── Barra de búsqueda ─────────────────────────────────────── */}
        <div className={`flex items-center gap-3 border-b px-4 py-3 transition-colors ${
          showIA ? 'border-violet-200 bg-violet-50/30' : 'border-slate-200'
        }`}>
          {showIA ? (
            <Sparkles size={18} className="text-violet-500 flex-shrink-0" />
          ) : (
            <Search size={18} className="text-slate-400 flex-shrink-0" />
          )}
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={showIA
              ? 'Pregunta lo que quieras sobre empleados, equipos, smartphones...'
              : 'Buscar empleados, equipos, documentos, publicaciones, extensiones y salas...'
            }
            className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
          />
          {puedeUsarIA && (
            <button
              type="button"
              onClick={toggleIAMode}
              title={iaMode ? 'Cambiar a búsqueda clásica' : 'Activar modo IA'}
              className={`flex-shrink-0 rounded-lg border px-2 py-1 text-xs transition-all ${
                showIA
                  ? 'border-violet-300 bg-violet-100 text-violet-700 hover:bg-violet-200'
                  : 'border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              <Sparkles size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={close}
            className="flex-shrink-0 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:bg-slate-50"
          >
            Esc
          </button>
        </div>

        {/* ─── Contenido ────────────────────────────────────────────── */}
        <div className="max-h-[65vh] overflow-y-auto">

          {/* Estado vacío (sin query) */}
          {query.trim().length < 2 && !iaResponse && !iaLoading && (
            <div className="space-y-3 p-6 text-sm text-slate-500">
              {showIA ? (
                <>
                  <p>Escribe tu pregunta y presiona Enter. Ejemplos:</p>
                  <div className="space-y-1.5">
                    {[
                      '¿Cuántas personas hay en el departamento de Costos?',
                      '¿Qué equipo tiene asignado Daniel?',
                      'Dame un resumen de los smartphones libres',
                      '¿Cuántos laptops hay asignados por sede?',
                    ].map((example) => (
                      <button
                        key={example}
                        type="button"
                        onClick={() => {
                          setQuery(example);
                          setIaMode(true);
                          executeIAQuery(example, []);
                        }}
                        className="block w-full text-left rounded-md bg-violet-50 px-3 py-2 text-sm text-violet-700 hover:bg-violet-100 transition-colors"
                      >
                        {example}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <p>Escribe al menos 2 caracteres para buscar en todos los módulos.</p>
                  <div className="flex flex-wrap gap-2">
                    {['Empleados', 'Equipos', 'Documentos', 'Publicaciones', 'Extensiones', 'Salas'].map((m) => (
                      <span key={m} className="rounded-md bg-slate-100 px-2 py-1">{m}</span>
                    ))}
                  </div>
                  {puedeUsarIA && (
                    <p className="text-xs text-slate-400 mt-2">
                      Tip: escribe una pregunta para activar el modo IA
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Loading IA */}
          {iaLoading && <IALoading />}

          {/* Error IA */}
          {iaError && !iaLoading && (
            <div className="p-6 text-sm text-red-600 bg-red-50/50">
              {iaError}
            </div>
          )}

          {/* Respuesta IA */}
          {iaResponse && !iaLoading && (
            <IAResult
              respuesta={iaResponse}
              onNavigate={handleIANavigate}
              onFollowUp={handleFollowUp}
            />
          )}

          {/* Búsqueda clásica: loading */}
          {loading && !showIA && (
            <div className="p-6 text-sm text-slate-500">Buscando...</div>
          )}

          {/* Búsqueda clásica: sin resultados */}
          {!loading && !showIA && query.trim().length >= 2 && groups.length === 0 && (
            <div className="space-y-2 p-6 text-sm text-slate-500">
              <p>No se encontraron resultados.</p>
              {puedeUsarIA && (
                <button
                  type="button"
                  onClick={() => {
                    setIaMode(true);
                    executeIAQuery(query.trim(), []);
                  }}
                  className="flex items-center gap-1.5 text-violet-600 hover:text-violet-700 transition-colors"
                >
                  <Sparkles size={14} />
                  Intentar con búsqueda IA
                </button>
              )}
            </div>
          )}

          {/* Búsqueda clásica: resultados */}
          {!loading && !showIA && groups.map((group) => {
            const Icon = GROUP_ICONS[group.key] || Search;
            return (
              <section key={group.key} className="border-b border-slate-100 last:border-b-0">
                <header className="flex items-center gap-2 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <Icon size={14} />
                  {group.label}
                  <span className="text-slate-400">({group.items.length})</span>
                </header>
                <div>
                  {group.items.map((item) => {
                    const absoluteIndex = results.findIndex((r) => r.id === item.id);
                    const selected = absoluteIndex === selectedIndex;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => openResult(item)}
                        className={`flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors ${
                          selected ? 'bg-blue-50' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-800">{item.title}</p>
                          {item.subtitle && (
                            <p className="truncate text-xs text-slate-500">{item.subtitle}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                          {item.meta && <span className="hidden md:inline truncate max-w-[180px]">{item.meta}</span>}
                          <ArrowRight size={14} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>

        {/* ─── Footer ───────────────────────────────────────────────── */}
        <footer className={`flex items-center justify-between border-t px-4 py-2 text-xs transition-colors ${
          showIA ? 'border-violet-100 text-violet-500' : 'border-slate-200 text-slate-500'
        }`}>
          <span className="flex items-center gap-1">
            <Command size={12} /> K para abrir
          </span>
          {showIA ? (
            <span className="flex items-center gap-1">
              <Sparkles size={10} /> Modo IA · Enter para consultar
            </span>
          ) : (
            <span>↑ ↓ para navegar, Enter para abrir</span>
          )}
        </footer>
      </div>
    </div>
  );
}

export default UniversalSearchModal;
